import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { heroData } from '../utils/data';
import { MdArrowBack, MdGamepad } from 'react-icons/md';
import {
  FaCheck, FaApple, FaAndroid, FaGooglePlay,
  FaUser, FaCalendarAlt, FaTag, FaChild, FaHourglassHalf,
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useLang } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';

const platformIcons = { iOS: FaApple, Android: FaAndroid };

const GameDetails = () => {
  const { slug } = useParams();
  const { lang } = useLang();
  const t = translations[lang].gameDetails;
  const tGD = translations[lang].gameData;

  const game = heroData.find(g => g.slug === slug || g.id.toString() === slug);
  const gameT = (game && tGD[game.id]) || {};

  if (!game) {
    return (
      <div className="w-full min-h-screen bg-gray-950 flex flex-col items-center justify-center text-center px-6">
        <div className="w-16 h-16 bg-gray-900 rounded-2xl flex items-center justify-center mb-4">
          <MdGamepad className="text-gray-700 text-2xl" />
        </div>
        <p className="text-gray-400 font-black text-lg">{t.notFoundTitle}</p>
        <Link to="/games" className="mt-3 text-xs text-headingColor hover:underline font-bold">{t.notFoundBack}</Link>
      </div>
    );
  }

  const displayFeatures = gameT.features || game.features;
  const displayTags = gameT.tags || game.tags;
  const displayTagline = gameT.tagline || game.tagline;
  const displayDesc = gameT.shortDescription || game.description;

  return (
    <div className="w-full min-h-screen bg-gray-950">

      {/* ── Banner ── */}
      <div className="relative w-full bg-gray-950 pt-10 pb-16 px-6 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)', backgroundSize: '50px 50px' }} />
        <div className="absolute right-0 top-0 w-[500px] h-[400px] bg-headingColor/8 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-950 to-transparent pointer-events-none" />

        <div className="relative max-w-7xl mx-auto">
          <Link to="/games" className="inline-flex items-center gap-1.5 text-gray-600 hover:text-white transition-colors text-xs font-bold tracking-widest uppercase mb-6">
            <MdArrowBack className="text-sm" /> {t.back}
          </Link>
          <div className="flex items-start gap-4 flex-wrap">
            <div>
              <h1 className="text-5xl md:text-6xl font-black text-white tracking-tight">{game.name}</h1>
              <p className="text-headingColor text-sm font-semibold italic mt-2">{displayTagline}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <span className="bg-headingColor/15 border border-headingColor/25 text-red-400 text-[10px] font-bold px-3 py-1 rounded-full tracking-widest uppercase">{game.genre}</span>
            {game.ageRating && <span className="bg-white/5 border border-white/10 text-gray-400 text-[10px] font-bold px-3 py-1 rounded-full">{t.ageLabel} {game.ageRating}</span>}
            {game.status && (
              <span className="inline-flex items-center gap-1.5 bg-yellow-500/10 border border-yellow-500/25 text-yellow-400 text-[10px] font-bold px-3 py-1 rounded-full tracking-widest uppercase">
                <FaHourglassHalf size={9} /> {game.status}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-3 gap-8"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >

          {/* ── Left sidebar ── */}
          <div className="flex flex-col gap-5">
            <div className="bg-gray-900 border border-white/5 rounded-2xl overflow-hidden">
              <img src={game.imageSrc} alt={game.name} className="w-full object-contain" />
            </div>

            {/* ── Oyunu Başlat butonu ── */}
            {game.playUrl && (
              <a
                href={game.playUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-headingColor hover:bg-red-700 text-white px-7 py-3.5 rounded-xl font-bold text-sm shadow-2xl shadow-red-950/50 transition-all duration-200 hover:-translate-y-0.5"
              >
                <MdGamepad className="text-lg" /> {t.playBtn}
              </a>
            )}

            <div className="bg-gray-900 border border-white/5 rounded-2xl p-5">
              <p className="text-gray-600 text-[10px] font-bold tracking-widest uppercase mb-3">{lang === 'tr' ? 'Çok Yakında' : 'Coming Soon'}</p>
              <div className="flex flex-col gap-2.5">
                <div className="flex items-center gap-3 bg-white/5 border border-white/8 rounded-xl px-4 py-3 cursor-default">
                  <FaApple className="text-gray-300 text-xl shrink-0" />
                  <div>
                    <p className="text-gray-500 text-[9px] tracking-widest uppercase">{lang === 'tr' ? 'Yakında' : 'Soon'}</p>
                    <p className="text-white text-sm font-bold">App Store</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white/5 border border-white/8 rounded-xl px-4 py-3 cursor-default">
                  <FaGooglePlay className="text-gray-300 text-lg shrink-0" />
                  <div>
                    <p className="text-gray-500 text-[9px] tracking-widest uppercase">{lang === 'tr' ? 'Yakında' : 'Soon'}</p>
                    <p className="text-white text-sm font-bold">Google Play</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 border border-white/5 rounded-2xl p-5">
              <p className="text-gray-600 text-[10px] font-bold tracking-widest uppercase mb-3">{t.platformsLabel}</p>
              <div className="flex flex-wrap gap-2">
                {game.platforms?.map(p => {
                  const Icon = platformIcons[p] || FaAndroid;
                  return (
                    <span key={p} className="inline-flex items-center gap-1.5 bg-white/5 border border-white/8 text-gray-300 text-xs font-medium px-3 py-1.5 rounded-full">
                      <Icon size={10} />{p}
                    </span>
                  );
                })}
              </div>
            </div>

            <div className="bg-gray-900 border border-white/5 rounded-2xl p-5">
              <p className="text-gray-600 text-[10px] font-bold tracking-widest uppercase mb-4">{t.gameInfoLabel}</p>
              <div className="flex flex-col gap-3.5">
                {[
                  { icon: <FaUser size={11} />, label: t.developer, value: game.developer },
                  { icon: <FaTag size={11} />, label: t.publisher, value: game.publisher },
                  { icon: <FaCalendarAlt size={11} />, label: t.releaseDate, value: game.releaseDate },
                  { icon: <FaChild size={11} />, label: t.ageRating, value: game.ageRating || t.everyAge },
                ].map(({ icon, label, value }) => value && (
                  <div key={label} className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 text-gray-600">
                      {icon}
                      <span className="text-xs">{label}</span>
                    </div>
                    <span className="text-gray-300 text-xs font-semibold text-right">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Right content ── */}
          <div className="lg:col-span-2 flex flex-col gap-7">

            <div className="bg-gray-900 border border-white/5 rounded-2xl p-6">
              <p className="text-gray-600 text-[10px] font-bold tracking-widest uppercase mb-5">{t.keyFeatures}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {displayFeatures?.map(f => (
                  <div key={f} className="flex items-center gap-3 bg-white/3 border border-white/5 rounded-xl px-4 py-3">
                    <div className="w-6 h-6 bg-headingColor/15 rounded-lg flex items-center justify-center shrink-0">
                      <FaCheck className="text-headingColor text-[10px]" />
                    </div>
                    <span className="text-gray-300 text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-900 border border-white/5 rounded-2xl p-6">
              <p className="text-gray-600 text-[10px] font-bold tracking-widest uppercase mb-5">{t.about}</p>
              <div className="text-gray-400 text-sm leading-loose whitespace-pre-line">
                {displayDesc}
              </div>
            </div>

            {displayTags?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {displayTags.map(tag => (
                  <span key={tag} className="bg-gray-900 border border-white/8 text-gray-500 text-xs font-bold tracking-widest uppercase px-3 py-1.5 rounded-lg hover:border-headingColor/30 hover:text-gray-300 transition-colors cursor-default">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default GameDetails;
